/**
 * Cookie consent (EB-559).
 *
 * The storefront loaded GA4 on first paint with nothing asked and no way to decline, while the
 * published privacy policy said the site used "cookies estrictamente necesarias para el
 * funcionamiento de la sesión y la seguridad". `_ga` is neither. This module is the half of the fix
 * that makes the sentence true; `PrivacyPolicyPage` §10 is the other half, and the two are meant to
 * be read together — if one changes, the other is wrong.
 *
 * **Two categories, not five.** Everything this site stores is either strictly necessary (the Auth0
 * session, the cart) or analytics (`_ga`, `_ga_<ID>`). A cookie panel offering granular toggles over
 * categories that do not exist would be theatre, and every extra switch is one more thing a shopper
 * has to read before buying a phone screen. The delivery municipality in `localStorage`
 * (`useDeliverySelection`) is deliberately counted as necessary: it holds a choice the shopper made
 * on screen, it never leaves the device, and dropping it would break the quote on the product page.
 */

export type ConsentDecision = "granted" | "denied";

/** `null` means nobody has been asked yet — distinct from a decision, and the reason the banner
 *  shows. Storing "denied" for someone who never chose would silently claim they refused. */
export type ConsentState = ConsentDecision | null;

const STORAGE_KEY = "biai.consent.analytics";
const STORAGE_DATE_KEY = "biai.consent.date";

/**
 * Bumped when the categories change, which re-asks everyone.
 *
 * A consent collected for one set of purposes cannot be carried over to a wider one, so this is the
 * mechanism for adding, say, an advertising pixel later: change the version, and every stored
 * decision stops counting.
 */
export const CONSENT_VERSION = "1";

/** Safari in private mode throws on both read and write, and a cookie preference is not worth a
 *  crashed storefront. Every failure degrades to "nobody has decided", which shows the banner
 *  again — annoying, and the safe direction to be wrong in. */
const readStored = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStored = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignored: the decision still holds for this page load.
  }
};

/**
 * What the shopper decided, or `null` if they have not been asked.
 *
 * The stored value carries the version it was given under, so a decision made against an older set
 * of categories reads back as `null` rather than as consent to something that did not exist when
 * they clicked.
 */
export const readConsent = (): ConsentState => {
  const raw = readStored(STORAGE_KEY);
  if (!raw) return null;

  const [version, decision] = raw.split(":");
  if (version !== CONSENT_VERSION) return null;

  return decision === "granted" || decision === "denied" ? decision : null;
};

/** When the current decision was made, for the preferences panel — consent with no date is not
 *  auditable, and "when did I agree to this?" is a question a shopper is entitled to ask. */
export const readConsentDate = (): string | null => readStored(STORAGE_DATE_KEY);

export const writeConsent = (decision: ConsentDecision) => {
  writeStored(STORAGE_KEY, `${CONSENT_VERSION}:${decision}`);
  writeStored(STORAGE_DATE_KEY, new Date().toISOString());
};

/**
 * Deletes the cookies GA4 set, on the shopper's own domain.
 *
 * Flipping Consent Mode to `denied` stops gtag *using* cookies; it does not remove the ones already
 * written. Someone who accepts, changes their mind, and then opens devtools to check would still
 * find `_ga` sitting there, which is exactly the kind of gap that makes a consent banner worthless.
 *
 * `_ga_<ID>` varies with the measurement ID, so the names are read off `document.cookie` rather
 * than guessed. Expiring on both the exact host and the dot-prefixed parent covers however the
 * cookie was scoped — a mismatched domain is a silent no-op.
 */
export const clearAnalyticsCookies = () => {
  const names = document.cookie
    .split(";")
    .map((entry) => entry.split("=")[0]?.trim())
    .filter((name): name is string => Boolean(name) && /^_ga/.test(name));

  const { hostname } = window.location;
  const domains = [hostname, `.${hostname}`, `.${hostname.split(".").slice(-2).join(".")}`];

  names.forEach((name) => {
    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
    });
    document.cookie = `${name}=; Max-Age=0; path=/`;
  });
};
